import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { getProducts, getCategories } from "@/lib/api";

const categoryMap = {
  "tes": "Tés",
  "cafes": "Cafés",
  "suplementos": "Suplementos",
  "cuidado-personal": "Cuidado Personal",
};

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const activeSlug = searchParams.get("categoria") || "";
  const activeCategory = categoryMap[activeSlug] || "";

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [prodRes, catRes] = await Promise.all([
          getProducts(activeCategory ? { category: activeCategory } : {}),
          getCategories(),
        ]);
        setProducts(prodRes.data);
        setCategories(catRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeCategory]);

  const filtered = search
    ? products.filter(
        (p) =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.description.toLowerCase().includes(search.toLowerCase())
      )
    : products;

  return (
    <div data-testid="catalog-page" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <span className="font-body text-xs font-semibold tracking-[0.2em] uppercase text-terracotta">Catálogo</span>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tighter font-medium text-forest mt-2">
            Nuestros Productos
          </h1>
          <p className="font-body text-base md:text-lg text-sage mt-3 max-w-xl">
            Explora la línea completa de productos naturales Vida Divina para tu bienestar integral.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-10">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-sage" />
            <input
              type="text"
              data-testid="catalog-search-input"
              placeholder="Buscar productos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-[#E5E0D8] bg-white/50 pl-11 pr-4 py-3 font-body text-sm text-forest transition-colors focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/20 placeholder:text-sage"
            />
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <SlidersHorizontal size={16} className="text-sage" />
            <button
              data-testid="filter-all"
              onClick={() => setSearchParams({})}
              className={`rounded-full px-4 py-2 text-sm font-body font-medium transition-all duration-200 ${
                !activeSlug
                  ? "bg-forest text-white"
                  : "bg-white text-sage border border-[#E5E0D8] hover:border-forest hover:text-forest"
              }`}
            >
              Todos
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                data-testid={`filter-${cat.slug}`}
                onClick={() => setSearchParams({ categoria: cat.slug })}
                className={`rounded-full px-4 py-2 text-sm font-body font-medium transition-all duration-200 ${
                  activeSlug === cat.slug
                    ? "bg-forest text-white"
                    : "bg-white text-sage border border-[#E5E0D8] hover:border-forest hover:text-forest"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-[2rem] p-5 border border-[#E5E0D8] animate-pulse">
                <div className="aspect-[4/3] rounded-2xl bg-sand mb-4" />
                <div className="h-5 bg-sand rounded w-3/4 mb-2" />
                <div className="h-4 bg-sand rounded w-full mb-4" />
                <div className="h-4 bg-sand rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filtered.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>
        ) : (
          <div data-testid="no-products-message" className="text-center py-20">
            <p className="font-heading text-2xl text-forest mb-2">No se encontraron productos</p>
            <p className="font-body text-sage">Intenta con otra búsqueda o categoría</p>
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="font-body text-sm text-sage">
            Mostrando {filtered.length} producto{filtered.length !== 1 ? "s" : ""}
            {activeCategory ? ` en ${activeCategory}` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}
